import React, { useEffect, useState } from "react";
import {
  Container, Row, Col, Card, CardBody, Nav, NavItem, NavLink,
  TabContent, TabPane, Input, Label, Button, Badge, Table, Alert, Spinner,
} from "reactstrap";
import classnames from "classnames";
import { useSelector, useDispatch } from "react-redux";
import { createSelector } from "reselect";
import { useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import moment from "moment";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { getUserProfile } from "store/account/actions";
import { editUserProfile } from "../../helpers/api_helper";
import {
  getMfaStatus, mfaEnrollStart, mfaEnrollConfirm,
  listApiKeys, createApiKey, rotateApiKey, revokeApiKey,
} from "../../helpers/settings_helper";
import ChangePassword from "./ChangePassword";

const AccountSettings = () => {
  const dispatch = useDispatch();
  const { search } = useLocation();
  const [activeTab, setActiveTab] = useState(
    new URLSearchParams(search).get("tab") === "api" ? "api" : "overview"
  );

  const sel = createSelector(
    (s) => s.User,
    (user) => ({ userProfile: user.userProfile })
  );
  const { userProfile } = useSelector(sel);

  const company = userProfile?.companies?.[0];
  const isAdmin = !!(company?.isAdmin ?? userProfile?.isCompanyAdmin);
  const companyId = userProfile?.companyId;

  // ───────────── Overview ─────────────
  const [form, setForm] = useState({ firstName: "", lastName: "", phone: "" });
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    if (userProfile) {
      setForm({
        firstName: userProfile.firstName || "",
        lastName: userProfile.lastName || "",
        phone: userProfile.phone || "",
      });
    }
  }, [userProfile]);

  const saveProfile = async () => {
    try {
      setSavingProfile(true);
      await editUserProfile(userProfile.id, form);
      dispatch(getUserProfile());
      Swal.fire("Saved", "Profile updated.", "success");
    } catch (e) {
      Swal.fire("Error", "Could not update profile.", "error");
    } finally {
      setSavingProfile(false);
    }
  };

  // ───────────── Security: password + MFA ─────────────
  const [pwOpen, setPwOpen] = useState(false);
  const [mfa, setMfa] = useState(null);
  const [enroll, setEnroll] = useState(null); // { secret, otpauthUri }
  const [mfaCode, setMfaCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [mfaBusy, setMfaBusy] = useState(false);

  const loadMfa = async () => {
    try { setMfa(await getMfaStatus()); } catch { setMfa({ enrolled: false }); }
  };
  useEffect(() => { loadMfa(); }, []);

  const startEnroll = async () => {
    try { setMfaBusy(true); setEnroll(await mfaEnrollStart()); }
    catch { Swal.fire("Error", "Could not start 2FA enrolment.", "error"); }
    finally { setMfaBusy(false); }
  };
  const confirmEnroll = async () => {
    try {
      setMfaBusy(true);
      const res = await mfaEnrollConfirm({ secret: enroll.secret, code: mfaCode });
      setRecoveryCodes(res?.recoveryCodes || []);
      setEnroll(null); setMfaCode("");
      loadMfa();
    } catch { Swal.fire("Error", "Invalid code — please try again.", "error"); }
    finally { setMfaBusy(false); }
  };

  // ───────────── API keys ─────────────
  const [keys, setKeys] = useState([]);
  const [keysLoading, setKeysLoading] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newEnv, setNewEnv] = useState("PRODUCTION");
  const [createdKey, setCreatedKey] = useState(null);

  const loadKeys = async () => {
    if (!companyId) return;
    try { setKeysLoading(true); setKeys(await listApiKeys(companyId)); }
    catch { setKeys([]); }
    finally { setKeysLoading(false); }
  };
  useEffect(() => { if (activeTab === "api") loadKeys(); }, [activeTab, companyId]);

  const doCreate = async () => {
    if (!newLabel.trim()) return;
    try {
      const res = await createApiKey(companyId, { label: newLabel.trim(), environment: newEnv });
      setCreatedKey(res?.plaintextKey || res?.fullKey || res?.key);
      setNewLabel("");
      loadKeys();
    } catch { Swal.fire("Error", "Could not create key.", "error"); }
  };
  const doRotate = async (id) => {
    const ok = await Swal.fire({
      title: "Rotate this key?",
      text: "The old key stops working immediately.",
      icon: "warning", showCancelButton: true, confirmButtonText: "Rotate",
    });
    if (!ok.isConfirmed) return;
    try {
      const res = await rotateApiKey(companyId, id);
      setCreatedKey(res?.plaintextKey || res?.fullKey || res?.key);
      loadKeys();
    } catch { Swal.fire("Error", "Could not rotate key.", "error"); }
  };
  const doRevoke = async (id) => {
    const ok = await Swal.fire({
      title: "Revoke this key?", icon: "warning",
      showCancelButton: true, confirmButtonText: "Revoke", confirmButtonColor: "#d33",
    });
    if (!ok.isConfirmed) return;
    try { await revokeApiKey(companyId, id); loadKeys(); }
    catch { Swal.fire("Error", "Could not revoke key.", "error"); }
  };

  document.title = "Account Settings | KenGen SMS Portal";

  const Field = ({ label, children }) => (
    <Col md={6} className="mb-3">
      <Label className="text-muted">{label}</Label>
      {children}
    </Col>
  );

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Settings" breadcrumbItem="Account Settings" />
        <Card>
          <CardBody>
            <Nav tabs className="mb-4">
              {["overview", "security", "api"].map((t) => (
                <NavItem key={t}>
                  <NavLink
                    className={classnames({ active: activeTab === t })}
                    onClick={() => setActiveTab(t)}
                    style={{ cursor: "pointer" }}
                  >
                    {t === "overview" ? "Overview" : t === "security" ? "Security" : "API Settings"}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>

            <TabContent activeTab={activeTab}>
              {/* ───────── OVERVIEW ───────── */}
              <TabPane tabId="overview">
                <Row>
                  <Field label="First name">
                    <Input value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
                  </Field>
                  <Field label="Last name">
                    <Input value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
                  </Field>
                  <Field label="Email">
                    <Input value={userProfile?.email || ""} disabled />
                    {userProfile?.isEmailVerified ? <Badge color="success" className="mt-1">Verified</Badge> : null}
                  </Field>
                  <Field label="Phone">
                    <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                    {userProfile?.isPhoneVerified ? <Badge color="success" className="mt-1">Verified</Badge> : null}
                  </Field>
                  <Field label="Department">
                    <Input value={userProfile?.department || "—"} disabled />
                  </Field>
                  <Field label="Company">
                    <Input value={company?.name || "—"} disabled />
                  </Field>
                  <Field label="Role">
                    <div><Badge color={isAdmin ? "primary" : "secondary"}>{isAdmin ? "Admin" : "Member"}</Badge></div>
                  </Field>
                  <Field label="Two-Factor Auth">
                    <div><Badge color={userProfile?.is2faEnabled ? "success" : "secondary"}>{userProfile?.is2faEnabled ? "Enabled" : "Disabled"}</Badge></div>
                  </Field>
                </Row>
                <Button color="primary" onClick={saveProfile} disabled={savingProfile || !userProfile}>
                  {savingProfile ? <Spinner size="sm" /> : "Save changes"}
                </Button>
              </TabPane>

              {/* ───────── SECURITY ───────── */}
              <TabPane tabId="security">
                <h5 className="mb-3">Password</h5>
                <Button color="primary" outline onClick={() => setPwOpen(true)}>Change Password</Button>
                <ChangePassword visible={pwOpen} toggle={() => setPwOpen(!pwOpen)} />

                <hr className="my-4" />
                <h5 className="mb-3">Two-Factor Authentication (TOTP)</h5>
                {mfa?.enrolled ? (
                  <Alert color="success">
                    2FA is <strong>enabled</strong>{mfa.method ? ` (${mfa.method})` : ""}.
                    {typeof mfa.recoveryCodesRemaining === "number" ? ` ${mfa.recoveryCodesRemaining} recovery codes remaining.` : ""}
                  </Alert>
                ) : recoveryCodes ? (
                  <Alert color="warning">
                    <p className="mb-2"><strong>Save these recovery codes</strong> — they are shown only once:</p>
                    <pre className="mb-0">{recoveryCodes.join("\n")}</pre>
                  </Alert>
                ) : enroll ? (
                  <div style={{ maxWidth: 440 }}>
                    <p className="text-muted mb-2">
                      Add this secret to your authenticator app (Google Authenticator, Authy, 1Password),
                      then enter the 6-digit code it shows.
                    </p>
                    <Label>Secret key</Label>
                    <Input readOnly value={enroll.secret} className="mb-3" onFocus={(e) => e.target.select()} />
                    <Label>6-digit code</Label>
                    <Input value={mfaCode} onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ""))} maxLength={6} placeholder="123456" className="mb-3" />
                    <Button color="primary" onClick={confirmEnroll} disabled={mfaBusy || mfaCode.length < 6}>
                      {mfaBusy ? <Spinner size="sm" /> : "Confirm & Enable"}
                    </Button>{" "}
                    <Button color="secondary" outline onClick={() => { setEnroll(null); setMfaCode(""); }}>Cancel</Button>
                  </div>
                ) : (
                  <>
                    <p className="text-muted">Add a second layer of security using an authenticator app.</p>
                    <Button color="primary" onClick={startEnroll} disabled={mfaBusy}>
                      {mfaBusy ? <Spinner size="sm" /> : "Enable 2FA"}
                    </Button>
                  </>
                )}
              </TabPane>

              {/* ───────── API SETTINGS ───────── */}
              <TabPane tabId="api">
                {!isAdmin && <Alert color="info">API keys are managed by a company admin.</Alert>}
                {createdKey && (
                  <Alert color="warning">
                    <p className="mb-1"><strong>Copy your new API key now</strong> — it won't be shown again:</p>
                    <code style={{ wordBreak: "break-all" }}>{createdKey}</code>
                    <div className="mt-2"><Button size="sm" color="secondary" onClick={() => setCreatedKey(null)}>Done</Button></div>
                  </Alert>
                )}
                <Row className="align-items-end mb-3">
                  <Col md={5} className="mb-2">
                    <Label>Key label</Label>
                    <Input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="e.g. Backend OTP service" />
                  </Col>
                  <Col md={4} className="mb-2">
                    <Label>Environment</Label>
                    <Input type="select" value={newEnv} onChange={(e) => setNewEnv(e.target.value)}>
                      <option value="PRODUCTION">Production</option>
                      <option value="SANDBOX">Sandbox</option>
                    </Input>
                  </Col>
                  <Col md={3} className="mb-2">
                    <Button color="primary" onClick={doCreate} disabled={!newLabel.trim() || !companyId}>Create key</Button>
                  </Col>
                </Row>
                <Table responsive className="table-nowrap mb-0">
                  <thead>
                    <tr><th>Label</th><th>Prefix</th><th>Environment</th><th>Status</th><th>Last used</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {keysLoading ? (
                      <tr><td colSpan={6}><Spinner size="sm" /> Loading…</td></tr>
                    ) : keys.length === 0 ? (
                      <tr><td colSpan={6} className="text-muted">No API keys yet.</td></tr>
                    ) : keys.map((k) => (
                      <tr key={k.id}>
                        <td>{k.label}</td>
                        <td><code>{k.keyPrefix}</code></td>
                        <td>{k.environment}</td>
                        <td><Badge color={k.status === "ACTIVE" ? "success" : "secondary"}>{k.status}</Badge></td>
                        <td>{k.lastUsedAt ? moment(k.lastUsedAt).format("DD MMM YYYY") : "—"}</td>
                        <td>
                          {k.status === "ACTIVE" && (
                            <>
                              <Button size="sm" color="warning" outline className="me-2" onClick={() => doRotate(k.id)}>Rotate</Button>
                              <Button size="sm" color="danger" outline onClick={() => doRevoke(k.id)}>Revoke</Button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TabPane>
            </TabContent>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default AccountSettings;
